import React, { useId, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import ActionButton from '../ActionButton';
import PortalDialog from '../PortalDialog';
import { CloseRemoveIcon } from '../shared_icons/AppIcons';
import { DOCUMENTS } from './Constants';
import legalDocumentStyles from '../../../../stylesheets/components/legalDocument.module.scss';

interface DocumentDialogProps {
  document: keyof typeof DOCUMENTS;
  onClose: () => void;
}

const DocumentDialog = ({ document, onClose }: DocumentDialogProps) => {
  const dialogId = useId();
  const theme = useTheme();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { title, path } = DOCUMENTS[document];

  const handleLoad = () => {
    setIsLoading(false);
    const frameDocument = frameRef.current?.contentDocument;
    if (frameDocument) {
      frameDocument.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
      });
    }
  };

  return (
    <PortalDialog
      dialogId={`legal-document-${dialogId}`}
      title={title}
      submitText="Close"
      acceptFunction={onClose}
      cancelFunction={onClose}
      hideTitle
      hideActions
      rootClassName={legalDocumentStyles.root}
      containerClassName={legalDocumentStyles.container}
      paperClassName={legalDocumentStyles.paper}
      contentClassName={legalDocumentStyles.content}
    >
      <div className={legalDocumentStyles.header}>
        <h2 className={legalDocumentStyles.title}>{title}</h2>
        <ActionButton
          className={legalDocumentStyles.closeButton}
          onClick={onClose}
          text=""
          icon={<CloseRemoveIcon />}
          buttonProps={{ 'aria-label': `Close ${title}` }}
        />
      </div>
      {isLoading && (
        <p className={legalDocumentStyles.loading} role="status">
          Loading {title}…
        </p>
      )}
      <iframe
        ref={frameRef}
        className={legalDocumentStyles.frame}
        title={title}
        src={`${path}?theme=${theme.palette.mode}`}
        sandbox="allow-same-origin"
        tabIndex={0}
        onLoad={handleLoad}
      />
    </PortalDialog>
  );
};

export default DocumentDialog;
